import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { UpgradePrompt } from '../components/FeatureGate';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Settings as SettingsIcon, 
  Building2, 
  Clock, 
  Save, 
  Globe, 
  Users as UsersIcon, 
  Search, 
  Trash2, 
  Shield, 
  UserCog, 
  User,
  MoreHorizontal,
  DollarSign,
  Percent,
  Brain,
  Key,
  Eye,
  EyeOff,
  ExternalLink,
  CheckCircle2,
  Power,
  CreditCard,
  Zap,
  TrendingUp,
  Check,
  X,
  Plus,
  Minus,
  ArrowDown,
  Crown,
  Loader2,
  Lock,
  HelpCircle,
  Edit,
  KeyRound,
  UserX,
  UserCheck,
  History,
  Copy,
  AlertCircle,
  Calculator,
  Link,
  Unlink,
  Gift,
  HardDrive,
  AlertTriangle
} from 'lucide-react';
import SupportTab from '../components/SupportTab';

// Currency options
const CURRENCIES = [
  { code: 'AUD', symbol: '$', name: 'Australian Dollar' },
  { code: 'NZD', symbol: '$', name: 'New Zealand Dollar' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'CAD', symbol: '$', name: 'Canadian Dollar' },
  { code: 'SGD', symbol: '$', name: 'Singapore Dollar' },
  { code: 'HKD', symbol: '$', name: 'Hong Kong Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
];

// Common tax types
const TAX_TYPES = [
  { name: 'GST', description: 'Goods and Services Tax' },
  { name: 'VAT', description: 'Value Added Tax' },
  { name: 'Sales Tax', description: 'Sales Tax' },
  { name: 'HST', description: 'Harmonized Sales Tax' },
  { name: 'None', description: 'No Tax' },
];

// Common timezones grouped by region
const TIMEZONES = [
  { group: 'Australia', zones: [
    { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
    { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
    { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
    { value: 'Australia/Perth', label: 'Perth (AWST)' },
    { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
    { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
    { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
  ]},
  { group: 'New Zealand', zones: [
    { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
    { value: 'Pacific/Chatham', label: 'Chatham Islands' },
  ]},
  { group: 'Asia', zones: [
    { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Seoul', label: 'Seoul (KST)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'Asia/Kolkata', label: 'India (IST)' },
    { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  ]},
  { group: 'Europe', zones: [
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
    { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
  ]},
  { group: 'Americas', zones: [
    { value: 'America/New_York', label: 'New York (EST/EDT)' },
    { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
    { value: 'America/Denver', label: 'Denver (MST/MDT)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
    { value: 'America/Toronto', label: 'Toronto (EST/EDT)' },
    { value: 'America/Vancouver', label: 'Vancouver (PST/PDT)' },
  ]},
  { group: 'UTC', zones: [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  ]},
];

const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2025)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2025)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2025-12-31)' },
  { value: 'DD-MMM-YYYY', label: 'DD-MMM-YYYY (31-Dec-2025)' },
];

import { useSearchParams, Link as RouterLink } from 'react-router-dom';

const Settings = () => {
  const { api, user } = useAuth();
  const { hasFeature, hasTierFeature } = useTier();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    // Check URL for tab parameter
    const tabParam = searchParams.get('tab');
    const validTabs = ['organization', 'users', 'billing', 'ai', 'accounting', 'timesheets', 'account', 'support'];
    return validTabs.includes(tabParam) ? tabParam : 'organization';
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testingApiKey, setTestingApiKey] = useState(false);
  const [apiKeyValid, setApiKeyValid] = useState(null);
  const [form, setForm] = useState({
    client_id: '',
    company_name: '',
    industry: 'crane_hire',
    timezone: 'Australia/Sydney',
    date_format: 'DD/MM/YYYY',
    default_working_hours: {
      start: '07:00',
      end: '17:00'
    },
    currency: 'AUD',
    currency_symbol: '$',
    tax_name: 'GST',
    tax_rate: 10,
    ai_enabled: false,
    openai_api_key: '',
    pay_cycle_type: 'weekly',
    pay_cycle_day: 1,
    pay_cycle_start_date: ''
  });

  // My Account / Password Change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  // User Management state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ username: '', email: '', full_name: '', role: 'dispatcher' });
  const [userFormLoading, setUserFormLoading] = useState(false);
  const [tempPasswordModal, setTempPasswordModal] = useState({ open: false, password: '', username: '' });
  const [activityModal, setActivityModal] = useState({ open: false, user: null, logs: [] });
  const [activityLoading, setActivityLoading] = useState(false);
  
  // Billing state
  const [billingData, setBillingData] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState(false);
  const [allTiers, setAllTiers] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [extraResourceQuantity, setExtraResourceQuantity] = useState(1);
  
  // Storage state
  const [storageData, setStorageData] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);

  // Xero/Accounting state
  const [xeroStatus, setXeroStatus] = useState({ connected: false, tenant_name: null, connected_at: null });
  const [xeroLoading, setXeroLoading] = useState(false);
  const [xeroConnecting, setXeroConnecting] = useState(false);
  const [xeroImporting, setXeroImporting] = useState(false);
  const [xeroImportResult, setXeroImportResult] = useState(null);
  const [invoiceNumberingSource, setInvoiceNumberingSource] = useState('accounting_package');
  const [connectedAccountingPackage, setConnectedAccountingPackage] = useState(null);
  const [savingInvoiceSetting, setSavingInvoiceSetting] = useState(false);
  
  // Timesheets settings state
  const [savingPayCycle, setSavingPayCycle] = useState(false);
  
  // MYOB state
  const [myobStatus, setMyobStatus] = useState({ connected: false, company_name: null, connected_at: null, needs_reauth: false, reauth_reason: null, product_type: null, has_cf_credentials: false });
  const [myobLoading, setMyobLoading] = useState(false);
  const [myobConnecting, setMyobConnecting] = useState(false);
  const [myobImporting, setMyobImporting] = useState(false);
  const [myobImportResult, setMyobImportResult] = useState(null);
  const [myobSettings, setMyobSettings] = useState({ product_type: null, cf_username: '', cf_password: '' });
  const [myobSettingsSaving, setMyobSettingsSaving] = useState(false);
  const [showMyobSetup, setShowMyobSetup] = useState(false);

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const response = await api.get(`/tenants/${user.client_id}`);
        setForm({
          client_id: response.data.client_id,
          company_name: response.data.company_name,
          industry: response.data.industry || 'crane_hire',
          timezone: response.data.timezone || 'Australia/Sydney',
          date_format: response.data.date_format || 'DD/MM/YYYY',
          default_working_hours: response.data.default_working_hours || { start: '07:00', end: '17:00' },
          currency: response.data.currency || 'AUD',
          currency_symbol: response.data.currency_symbol || '$',
          tax_name: response.data.tax_name || 'GST',
          tax_rate: response.data.tax_rate ?? 10,
          ai_enabled: response.data.ai_enabled ?? false,
          openai_api_key: response.data.openai_api_key || '',
          pay_cycle_type: response.data.pay_cycle_type || 'weekly',
          pay_cycle_day: response.data.pay_cycle_day ?? 1,
          pay_cycle_start_date: response.data.pay_cycle_start_date || ''
        });
        // If API key exists, consider it valid (already saved)
        if (response.data.openai_api_key) {
          setApiKeyValid(true);
        }
      } catch (error) {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchTenant();
  }, [api, user.client_id]);

  // Update URL when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'organization') {
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tab);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Listen for URL changes (e.g., from browser back/forward)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const validTabs = ['organization', 'users', 'billing', 'ai', 'accounting', 'timesheets', 'account', 'support'];
    if (validTabs.includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
    }
    
    // Handle Xero OAuth callback parameters
    const xeroSuccess = searchParams.get('xero_success');
    const xeroError = searchParams.get('xero_error');
    
    if (xeroSuccess === 'true') {
      toast.success('Xero connected successfully!');
      fetchXeroStatus();
      fetchMyobStatus();
      // Clean up URL
      searchParams.delete('xero_success');
      setSearchParams(searchParams, { replace: true });
    }
    
    if (xeroError) {
      const errorMessages = {
        'token_exchange_failed': 'Failed to connect to Xero. Please try again.',
        'connections_failed': 'Failed to get Xero organizations. Please try again.',
        'no_organizations': 'No Xero organizations found. Please ensure you have access to at least one organization.'
      };
      toast.error(errorMessages[xeroError] || 'Xero connection failed. Please try again.');
      // Clean up URL
      searchParams.delete('xero_error');
      setSearchParams(searchParams, { replace: true });
    }
    
    // Handle MYOB OAuth callback parameters
    const myobSuccess = searchParams.get('myob_success');
    const myobError = searchParams.get('myob_error');
    
    if (myobSuccess === 'true') {
      toast.success('MYOB connected successfully!');
      fetchMyobStatus();
      fetchXeroStatus();
      // Clean up URL
      searchParams.delete('myob_success');
      setSearchParams(searchParams, { replace: true });
    }
    
    if (myobError) {
      const errorMessages = {
        'token_exchange_failed': 'Failed to connect to MYOB. Please try again.',
        'company_files_failed': 'Failed to get MYOB company files. Please try again.',
        'no_company_files': 'No MYOB company files found. Please ensure you have access to at least one company file.',
        'invalid_state': 'Invalid session. Please try connecting again.',
        'missing_params': 'Missing authorization parameters. Please try again.'
      };
      toast.error(errorMessages[myobError] || 'MYOB connection failed. Please try again.');
      // Clean up URL
      searchParams.delete('myob_error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams]);

  // Fetch Xero connection status and accounting settings
  const fetchXeroStatus = async () => {
    try {
      setXeroLoading(true);
      const response = await api.get('/xero/status');
      setXeroStatus(response.data);
      
      // Also fetch tenant settings for invoice numbering
      const tenantResponse = await api.get(`/tenants/${user.client_id}`);
      setInvoiceNumberingSource(tenantResponse.data.invoice_numbering_source || 'accounting_package');
      setConnectedAccountingPackage(tenantResponse.data.connected_accounting_package || null);
    } catch (error) {
      console.error('Failed to fetch Xero status:', error);
    } finally {
      setXeroLoading(false);
    }
  };
  
  // Fetch MYOB connection status
  const fetchMyobStatus = async () => {
    try {
      setMyobLoading(true);
      const response = await api.get('/myob/status');
      setMyobStatus(response.data);
      
      // Also fetch tenant settings
      const tenantResponse = await api.get(`/tenants/${user.client_id}`);
      setInvoiceNumberingSource(tenantResponse.data.invoice_numbering_source || 'accounting_package');
      setConnectedAccountingPackage(tenantResponse.data.connected_accounting_package || null);
    } catch (error) {
      console.error('Failed to fetch MYOB status:', error);
    } finally {
      setMyobLoading(false);
    }
  };

  // Save invoice numbering setting
  const handleSaveInvoiceNumberingSetting = async (value) => {
    try {
      setSavingInvoiceSetting(true);
      await api.put(`/tenants/${user.client_id}`, {
        ...form,
        invoice_numbering_source: value
      });
      setInvoiceNumberingSource(value);
      toast.success('Invoice numbering setting saved');
    } catch (error) {
      toast.error('Failed to save setting');
    } finally {
      setSavingInvoiceSetting(false);
    }
  };

  // Connect to Xero
  const handleConnectXero = async () => {
    try {
      setXeroConnecting(true);
      const response = await api.get('/xero/connect');
      // Redirect to Xero authorization
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate Xero connection');
      setXeroConnecting(false);
    }
  };

  // Disconnect Xero
  const handleDisconnectXero = async () => {
    try {
      await api.post('/xero/disconnect');
      setXeroStatus({ connected: false, tenant_name: null, connected_at: null });
      setConnectedAccountingPackage(null);
      toast.success('Xero disconnected successfully');
    } catch (error) {
      toast.error('Failed to disconnect Xero');
    }
  };

  // Import customers from Xero
  const handleImportCustomersFromXero = async () => {
    try {
      setXeroImporting(true);
      setXeroImportResult(null);
      const response = await api.post('/xero/import-customers');
      setXeroImportResult(response.data);
      toast.success(`Imported ${response.data.imported} customers, linked ${response.data.linked} existing`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import customers from Xero');
    } finally {
      setXeroImporting(false);
    }
  };
  
  // Connect to MYOB
  const handleConnectMyob = async () => {
    try {
      setMyobConnecting(true);
      const response = await api.get('/myob/connect');
      // Redirect to MYOB authorization
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate MYOB connection');
      setMyobConnecting(false);
    }
  };

  // Disconnect MYOB
  const handleDisconnectMyob = async () => {
    try {
      await api.post('/myob/disconnect');
      setMyobStatus({ connected: false, company_name: null, connected_at: null });
      setConnectedAccountingPackage(null);
      toast.success('MYOB disconnected successfully');
    } catch (error) {
      toast.error('Failed to disconnect MYOB');
    }
  };

  // Import customers from MYOB
  const handleImportCustomersFromMyob = async () => {
    try {
      setMyobImporting(true);
      setMyobImportResult(null);
      const response = await api.post('/myob/import-customers');
      setMyobImportResult(response.data);
      toast.success(`Imported ${response.data.imported} customers, updated ${response.data.updated} existing`);
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Failed to import customers from MYOB';
      toast.error(errorDetail);
      
      // If authentication expired, refresh the status to show reconnect option
      if (errorDetail.toLowerCase().includes('authentication expired') || 
          errorDetail.toLowerCase().includes('reconnect')) {
        fetchMyobStatus();
      }
    } finally {
      setMyobImporting(false);
    }
  };

  // Fetch MYOB settings
  const fetchMyobSettings = async () => {
    try {
      const response = await api.get('/myob/settings');
      setMyobSettings({
        product_type: response.data.product_type,
        cf_username: response.data.cf_username || '',
        cf_password: ''  // Never returned from API for security
      });
    } catch (error) {
      console.error('Failed to fetch MYOB settings:', error);
    }
  };

  // Save MYOB settings and initiate connection
  const handleSaveMyobSettingsAndConnect = async () => {
    if (!myobSettings.product_type) {
      toast.error('Please select your MYOB product type');
      return;
    }
    
    if (myobSettings.product_type === 'accountright' && (!myobSettings.cf_username || !myobSettings.cf_password)) {
      toast.error('Please enter your company file username and password');
      return;
    }
    
    try {
      setMyobSettingsSaving(true);
      
      // Save settings first
      await api.post('/myob/settings', {
        product_type: myobSettings.product_type,
        cf_username: myobSettings.product_type === 'accountright' ? myobSettings.cf_username : null,
        cf_password: myobSettings.product_type === 'accountright' ? myobSettings.cf_password : null
      });
      
      // Then initiate OAuth connection
      const response = await api.get('/myob/connect');
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save MYOB settings');
      setMyobSettingsSaving(false);
    }
  };

  // Start MYOB setup flow
  const handleStartMyobSetup = () => {
    fetchMyobSettings();
    setShowMyobSetup(true);
  };

  // Fetch Xero and MYOB status when accounting tab is active
  useEffect(() => {
    if (activeTab === 'accounting') {
      fetchXeroStatus();
      fetchMyobStatus();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      // 403 is expected for non-admin users
      if (error.response?.status !== 403) {
        toast.error('Failed to fetch users');
      }
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
      // Also fetch billing data to check user limits
      if (!billingData) {
        fetchBillingData();
      }
    }
  }, [activeTab, api]);

  // Billing data fetch
  const fetchBillingData = async () => {
    setBillingLoading(true);
    setStorageLoading(true);
    setBillingError(false);
    try {
      // Use Promise.allSettled to handle partial failures gracefully
      // (e.g., non-admin users can't access /billing/transactions)
      const [usageRes, tiersRes, transactionsRes, storageRes] = await Promise.allSettled([
        api.get('/billing/usage'),
        api.get('/billing/tiers'),
        api.get('/billing/transactions'),
        api.get('/storage/usage')
      ]);
      
      // Set billing data if usage call succeeded
      if (usageRes.status === 'fulfilled') {
        setBillingData(usageRes.value.data);
      } else {
        console.error('Failed to fetch billing usage:', usageRes.reason);
        setBillingError(true);
        toast.error('Failed to load billing data');
      }
      
      // Set tiers if call succeeded
      if (tiersRes.status === 'fulfilled') {
        setAllTiers(tiersRes.value.data.tiers);
      }
      
      // Set transactions if call succeeded (may fail for non-admin users)
      if (transactionsRes.status === 'fulfilled') {
        setTransactions(transactionsRes.value.data);
      }
      // Don't show error for transactions - it's expected to fail for non-admins
      
      // Set storage data if call succeeded
      if (storageRes.status === 'fulfilled') {
        setStorageData(storageRes.value.data);
      }
    } catch (error) {
      setBillingError(true);
      toast.error('Failed to load billing data');
    } finally {
      setBillingLoading(false);
      setStorageLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'billing') {
      fetchBillingData();
    }
  }, [activeTab, api]);

  // Check for session_id in URL (returning from Stripe)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const tab = urlParams.get('tab');
    const cancelled = urlParams.get('cancelled');
    
    if (tab === 'billing') {
      setActiveTab('billing');
    }
    
    if (sessionId) {
      // Poll for payment status
      const checkPaymentStatus = async () => {
        try {
          const response = await api.get(`/billing/checkout/status/${sessionId}`);
          if (response.data.status === 'paid') {
            toast.success('Payment successful! Your account has been updated.');
            fetchBillingData();
          } else if (response.data.status === 'expired') {
            toast.error('Payment session expired. Please try again.');
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
        }
      };
      checkPaymentStatus();
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (cancelled) {
      toast.info('Payment cancelled');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Checkout handlers
  const handleSubscriptionUpgrade = async (tier) => {
    setCheckoutLoading(true);
    try {
      const response = await api.post('/billing/checkout/subscription', {
        tier: tier,
        origin_url: window.location.origin
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  const handleChangePlan = async (newTier) => {
    // Determine if this is an upgrade or downgrade
    const tierOrder = { basic: 0, advanced: 1, professional: 2 };
    const currentTierLevel = tierOrder[billingData?.tier] || 0;
    const newTierLevel = tierOrder[newTier] || 0;
    const isUpgrade = newTierLevel > currentTierLevel;
    
    const tierName = newTier.charAt(0).toUpperCase() + newTier.slice(1);
    
    let confirmMessage;
    if (isUpgrade) {
      confirmMessage = `Upgrade to ${tierName} plan?\n\n• New features will be available immediately\n• You'll be charged the prorated difference for this billing period\n• Extra operators and users will be migrated to new pricing`;
    } else {
      confirmMessage = `Downgrade to ${tierName} plan?\n\n• You'll keep your current ${billingData?.tier_name || 'plan'} features until the end of your billing period\n• The new plan will take effect at your next billing cycle\n• Extra operators and users will be migrated to new pricing`;
    }
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    setCheckoutLoading(true);
    try {
      const response = await api.post('/billing/change-plan', { tier: newTier });
      
      // Show appropriate success message based on change type
      if (response.data.change_type === 'upgrade') {
        toast.success(`Upgraded to ${tierName}! Prorated charges have been applied.`);
      } else {
        toast.success(`Downgrade scheduled. You'll switch to ${tierName} on ${response.data.effective_date || 'your next billing cycle'}.`);
      }
      
      // Refresh billing data
      const billingRes = await api.get('/billing/usage');
      setBillingData(billingRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change plan');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleModifyExtras = async (resourceType, newQuantity) => {
    if (!billingData) return;
    const currentExtra = resourceType === 'operator' ? (billingData.operators_extra || 0) : (billingData.users_extra || 0);
    
    if (newQuantity < currentExtra) {
      if (!window.confirm(`Are you sure you want to reduce extra ${resourceType}s from ${currentExtra} to ${newQuantity}? Your subscription will be updated.`)) {
        return;
      }
    }
    
    setCheckoutLoading(true);
    try {
      const response = await api.post('/billing/modify-extras', {
        resource_type: resourceType,
        quantity: newQuantity
      });
      toast.success(response.data.message);
      // Refresh billing data
      const billingRes = await api.get('/billing/usage');
      setBillingData(billingRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to modify subscription');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handlePurchaseExtra = async (resourceType, quantity) => {
    setCheckoutLoading(true);
    try {
      const response = await api.post('/billing/checkout/extra-resource', {
        resource_type: resourceType,
        quantity: quantity,
        origin_url: window.location.origin
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(false);
    }
  };

  const handlePurchaseStorage = async () => {
    setCheckoutLoading(true);
    try {
      const response = await api.post('/storage/purchase-addon');
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Storage add-on is not currently available');
      setCheckoutLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/tenants/${user.client_id}`, form);
      toast.success('Settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // User Management handlers
  const handleOpenUserModal = (userToEdit = null) => {
    if (userToEdit) {
      setEditingUser(userToEdit);
      setUserForm({
        username: userToEdit.username,
        email: userToEdit.email || '',
        full_name: userToEdit.full_name,
        role: userToEdit.role
      });
    } else {
      setEditingUser(null);
      setUserForm({ username: '', email: '', full_name: '', role: 'dispatcher' });
    }
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setUserModalOpen(false);
    setEditingUser(null);
    setUserForm({ username: '', email: '', full_name: '', role: 'dispatcher' });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserFormLoading(true);
    try {
      const response = await api.post('/users', userForm);
      toast.success('User created successfully');
      setTempPasswordModal({
        open: true,
        password: response.data.temp_password,
        username: response.data.username
      });
      handleCloseUserModal();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setUserFormLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setUserFormLoading(true);
    try {
      await api.put(`/users/${editingUser.id}`, {
        email: userForm.email,
        full_name: userForm.full_name,
        role: userForm.role
      });
      toast.success('User updated successfully');
      handleCloseUserModal();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setUserFormLoading(false);
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await api.post(`/users/${userId}/reset-password`);
      toast.success('Password reset successfully');
      setTempPasswordModal({
        open: true,
        password: response.data.temp_password,
        username: response.data.username
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await api.put(`/users/${userId}/status`, { is_active: newStatus });
      toast.success(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleViewActivity = async (userToView) => {
    setActivityLoading(true);
    setActivityModal({ open: true, user: userToView, logs: [] });
    try {
      const response = await api.get(`/users/${userToView.id}/activity`);
      setActivityModal({ open: true, user: response.data.user, logs: response.data.activity_logs });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load activity logs');
    } finally {
      setActivityLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validation
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/\d/.test(passwordForm.newPassword)) {
      toast.error('Password must contain at least one number');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const canAddMoreUsers = () => {
    if (!billingData) return true;
    return (billingData.users_used || 0) < (billingData.users_total_limit || 1);
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return (
          <Badge className="bg-purple-100 text-purple-800">
            <Shield className="h-3 w-3 mr-1" />
            Admin
          </Badge>
        );
      case 'dispatcher':
        return (
          <Badge className="bg-blue-100 text-blue-800">
            <UserCog className="h-3 w-3 mr-1" />
            Dispatcher
          </Badge>
        );
      case 'operator':
        return (
          <Badge variant="secondary">
            <User className="h-3 w-3 mr-1" />
            Operator
          </Badge>
        );
      default:
        return <Badge variant="secondary">{role}</Badge>;
    }
  };

  const getStatusBadge = (isActive) => {
    if (isActive === false) {
      return (
        <Badge variant="secondary" className="bg-red-100 text-red-800">
          <UserX className="h-3 w-3 mr-1" />
          Inactive
        </Badge>
      );
    }
    return null;
  };

  const formatActivityAction = (action) => {
    const actionMap = {
      'created': { label: 'Account Created', color: 'text-green-600' },
      'updated': { label: 'Details Updated', color: 'text-blue-600' },
      'password_reset': { label: 'Password Reset', color: 'text-orange-600' },
      'activated': { label: 'Activated', color: 'text-green-600' },
      'deactivated': { label: 'Deactivated', color: 'text-red-600' },
      'deleted': { label: 'Deleted', color: 'text-red-600' },
      'login': { label: 'Logged In', color: 'text-gray-600' }
    };
    return actionMap[action] || { label: action, color: 'text-gray-600' };
  };

  const filteredUsers = users.filter(u =>
    (u.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.username || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSavePayCycle = async () => {
    setSavingPayCycle(true);
    try {
      await api.put(`/tenants/${user.client_id}`, {
        pay_cycle_type: form.pay_cycle_type,
        pay_cycle_day: form.pay_cycle_day,
        pay_cycle_start_date: form.pay_cycle_start_date || null
      });
      toast.success('Pay cycle settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save pay cycle settings');
    } finally {
      setSavingPayCycle(false);
    }
  };

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your organization settings and users</p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="organization" data-testid="tab-organization">
            <Building2 className="h-4 w-4 mr-2" />
            Organization
          </TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai" className="relative">
            <Brain className="h-4 w-4 mr-2" />
            AI Integration
            {!hasTierFeature('ai_features') && <Lock className="h-3 w-3 ml-1 text-muted-foreground" />}
          </TabsTrigger>
          <TabsTrigger value="accounting" data-testid="tab-accounting">
            <Calculator className="h-4 w-4 mr-2" />
            Accounting
          </TabsTrigger>
          <TabsTrigger value="timesheets" data-testid="tab-timesheets">
            <Clock className="h-4 w-4 mr-2" />
            Timesheets
          </TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">
            <CreditCard className="h-4 w-4 mr-2" />
            Billing
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">
            <UsersIcon className="h-4 w-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account">
            <User className="h-4 w-4 mr-2" />
            My Account
          </TabsTrigger>
          <TabsTrigger value="support" data-testid="tab-support">
            <HelpCircle className="h-4 w-4 mr-2" />
            Support
          </TabsTrigger>
        </TabsList>

        {/* Organization Settings Tab */}
        <TabsContent value="organization" className="mt-6">
          <form onSubmit={handleSubmit}>
            <div className="grid gap-6">
              {/* Company Information */}
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Company Information
                  </CardTitle>
                  <CardDescription>Basic details about your organization</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-overline">Client ID</Label>
                      <Input
                        value={form.client_id}
                        disabled
                        className="font-mono bg-muted"
                        data-testid="settings-client-id"
                      />
                      <p className="text-xs text-muted-foreground">
                        Your unique organization identifier (cannot be changed)
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Company Name</Label>
                      <Input
                        value={form.company_name}
                        onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                        placeholder="Your Company Name"
                        required
                        data-testid="settings-company-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Industry</Label>
                      <Select
                        value={form.industry || 'crane_hire'}
                        onValueChange={(value) => setForm({ ...form, industry: value })}
                      >
                        <SelectTrigger data-testid="settings-industry">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="crane_hire">Crane Hire</SelectItem>
                          <SelectItem value="earthmoving">Earthmoving</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Determines available equipment types and features
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Location & Timezone */}
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Location & Time Zone
                  </CardTitle>
                  <CardDescription>Set your timezone for accurate date and time display</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-overline">Time Zone</Label>
                      <Select
                        value={form.timezone}
                        onValueChange={(value) => setForm({ ...form, timezone: value })}
                      >
                        <SelectTrigger data-testid="settings-timezone">
                          <SelectValue placeholder="Select timezone" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIMEZONES.map((group) => (
                            <div key={group.group}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted">
                                {group.group}
                              </div>
                              {group.zones.map((zone) => (
                                <SelectItem key={zone.value} value={zone.value}>
                                  {zone.label}
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Current time: {new Date().toLocaleTimeString('en-AU', { timeZone: form.timezone, hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Date Format</Label>
                      <Select
                        value={form.date_format}
                        onValueChange={(value) => setForm({ ...form, date_format: value })}
                      >
                        <SelectTrigger data-testid="settings-date-format">
                          <SelectValue placeholder="Select date format" />
                        </SelectTrigger>
                        <SelectContent>
                          {DATE_FORMATS.map((format) => (
                            <SelectItem key={format.value} value={format.value}>
                              {format.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Working Hours */}
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Default Working Hours
                  </CardTitle>
                  <CardDescription>Standard operating hours for your business</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 max-w-md">
                    <div className="space-y-2">
                      <Label className="label-overline">Start Time</Label>
                      <Input
                        type="time"
                        value={form.default_working_hours.start}
                        onChange={(e) => setForm({
                          ...form,
                          default_working_hours: { ...form.default_working_hours, start: e.target.value }
                        })}
                        className="font-mono"
                        data-testid="settings-start-time"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">End Time</Label>
                      <Input
                        type="time"
                        value={form.default_working_hours.end}
                        onChange={(e) => setForm({
                          ...form,
                          default_working_hours: { ...form.default_working_hours, end: e.target.value }
                        })}
                        className="font-mono"
                        data-testid="settings-end-time"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Currency & Tax */}
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Currency & Tax
                  </CardTitle>
                  <CardDescription>Configure currency and tax settings for invoicing</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-overline">Currency</Label>
                      <Select
                        value={form.currency}
                        onValueChange={(value) => {
                          const currency = CURRENCIES.find(c => c.code === value);
                          setForm({ 
                            ...form, 
                            currency: value,
                            currency_symbol: currency?.symbol || '$'
                          });
                        }}
                      >
                        <SelectTrigger data-testid="settings-currency">
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map((currency) => (
                            <SelectItem key={currency.code} value={currency.code}>
                              {currency.symbol} {currency.code} - {currency.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Currency Symbol</Label>
                      <Input
                        value={form.currency_symbol}
                        onChange={(e) => setForm({ ...form, currency_symbol: e.target.value })}
                        placeholder="$"
                        className="font-mono w-20"
                        maxLength={3}
                        data-testid="settings-currency-symbol"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-overline">Tax Type</Label>
                      <Select
                        value={form.tax_name}
                        onValueChange={(value) => setForm({ ...form, tax_name: value })}
                      >
                        <SelectTrigger data-testid="settings-tax-name">
                          <SelectValue placeholder="Select tax type" />
                        </SelectTrigger>
                        <SelectContent>
                          {TAX_TYPES.map((tax) => (
                            <SelectItem key={tax.name} value={tax.name}>
                              {tax.name} - {tax.description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Tax Rate (%)</Label>
                      <div className="relative">
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={form.tax_rate}
                          onChange={(e) => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })}
                          placeholder="10"
                          className="font-mono pr-8"
                          data-testid="settings-tax-rate"
                        />
                        <Percent className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      </div>
                      {form.tax_name !== 'None' && form.tax_rate > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {form.tax_name} at {form.tax_rate}% will be added to docket totals
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* System Info */}
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <SettingsIcon className="h-5 w-5" />
                    System Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="label-overline">Current User</p>
                      <p className="font-medium">{user?.full_name}</p>
                    </div>
                    <div>
                      <p className="label-overline">Role</p>
                      <p className="font-medium capitalize">{user?.role}</p>
                    </div>
                    <div>
                      <p className="label-overline">Username</p>
                      <p className="font-mono">{user?.username}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button type="submit" className="btn-volt" disabled={saving} data-testid="save-settings-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </div>
          </form>
        </TabsContent>

        {/* AI Integration Tab */}
        <TabsContent value="ai" className="mt-6">
          {!hasTierFeature('ai_features') ? (
            <UpgradePrompt 
              feature="ai_features"
              title="AI Integration"
              description="Unlock powerful AI features including Docket OCR scanning, Smart Operator Suggestions, and intelligent Compliance Alerts. Available exclusively on the Professional plan."
            />
          ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid gap-6">
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Brain className="h-5 w-5" />
                    AI Features
                  </CardTitle>
                  <CardDescription>
                    Enable AI-powered features like Docket OCR, Smart Scheduling, and Compliance Alerts
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Master AI Enable/Disable Toggle */}
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-gradient-to-r from-[#ffd108]/5 to-transparent">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${form.ai_enabled ? 'bg-green-500/20' : 'bg-muted'}`}>
                        <Power className={`h-5 w-5 ${form.ai_enabled ? 'text-green-500' : 'text-muted-foreground'}`} />
                      </div>
                      <div>
                        <Label className="text-base font-semibold">AI Features</Label>
                        <p className="text-sm text-muted-foreground">
                          {form.ai_enabled ? 'AI features are enabled for your organization' : 'Enable to use AI-powered features'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={form.ai_enabled}
                      onCheckedChange={(checked) => setForm({ ...form, ai_enabled: checked })}
                      data-testid="ai-enabled-toggle"
                    />
                  </div>

                  <div className={`p-4 bg-muted/50 rounded-lg transition-opacity ${!form.ai_enabled ? 'opacity-50' : ''}`}>
                    <h4 className="font-semibold mb-2">Available AI Features:</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li className="flex items-center gap-2">
                        <Badge variant={form.ai_enabled ? 'default' : 'outline'} className={form.ai_enabled ? 'bg-green-500' : ''}>
                          {form.ai_enabled ? 'Active' : 'Disabled'}
                        </Badge>
                        <strong>Docket OCR</strong> - Scan paper dockets and auto-extract data
                      </li>
                      <li className="flex items-center gap-2">
                        <Badge variant={form.ai_enabled ? 'default' : 'outline'} className={form.ai_enabled ? 'bg-green-500' : ''}>
                          {form.ai_enabled ? 'Active' : 'Disabled'}
                        </Badge>
                        <strong>Compliance Alerts</strong> - AI-powered expiry warnings
                      </li>
                      <li className="flex items-center gap-2">
                        <Badge variant={form.ai_enabled ? 'default' : 'outline'} className={form.ai_enabled ? 'bg-green-500' : ''}>
                          {form.ai_enabled ? 'Active' : 'Disabled'}
                        </Badge>
                        <strong>Smart Suggestions</strong> - Intelligent operator/equipment recommendations
                      </li>
                    </ul>
                  </div>

                  {form.ai_enabled && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="label-overline flex items-center gap-2">
                          <Key className="h-4 w-4" />
                          OpenAI API Key
                        </Label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Input
                              type={showApiKey ? 'text' : 'password'}
                              value={form.openai_api_key}
                              onChange={(e) => {
                                setForm({ ...form, openai_api_key: e.target.value });
                                setApiKeyValid(null);
                              }}
                              placeholder="sk-..."
                              className="font-mono pr-10"
                              data-testid="settings-openai-key"
                            />
                            <button
                              type="button"
                              onClick={() => setShowApiKey(!showApiKey)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                          {apiKeyValid === true && (
                            <div className="flex items-center text-green-600">
                              <CheckCircle2 className="h-5 w-5" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Your API key is stored securely and only used for AI features within your organization.
                        </p>
                      </div>

                      <div className="flex items-center gap-4 pt-2">
                        <a
                          href="https://platform.openai.com/api-keys"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                        >
                          Get an API key from OpenAI
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>

                      {!form.openai_api_key && (
                        <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                          <p className="text-sm text-amber-800">
                            <strong>Note:</strong> Some AI features (like Docket OCR) require an OpenAI API key. Smart Suggestions work without a key.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {!form.ai_enabled && (
                    <div className="p-4 border border-muted bg-muted/30 rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        AI features are currently disabled. Enable the toggle above to access Smart Suggestions, Docket OCR, and Compliance Alerts.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button type="submit" className="btn-volt" disabled={saving} data-testid="save-ai-settings-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </div>
          </form>
          )}
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          {user?.role !== 'admin' ? (
            <Card className="card-industrial">
              <CardContent className="py-12 text-center">
                <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">User management is only available to administrators.</p>
              </CardContent>
            </Card>
          ) : (
          <div className="space-y-6">
            {/* User Limits Banner */}
            {billingData && (
              <Card className={`card-industrial ${(billingData.users_used || 0) >= (billingData.users_total_limit || 1) ? 'border-destructive' : ''}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <UsersIcon className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">
                          User Seats: {billingData.users_used || 0} / {billingData.users_total_limit || 0}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {billingData.tier_name || 'Basic'} Plan ({billingData.users_limit || 0} included
                          {(billingData.users_extra || 0) > 0 && ` + ${billingData.users_extra} extra`})
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(billingData.users_used || 0) >= (billingData.users_total_limit || 1) && (
                        <Badge variant="destructive" className="mr-2">Limit Reached</Badge>
                      )}
                      <Button 
                        onClick={() => handleOpenUserModal()}
                        disabled={!canAddMoreUsers()}
                        className="btn-volt"
                        data-testid="add-user-btn"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add User
                      </Button>
                    </div>
                  </div>
                  {(billingData.users_used || 0) >= (billingData.users_total_limit || 1) && (
                    <p className="text-xs text-destructive mt-2">
                      You have reached your user limit. Purchase additional seats or upgrade your plan in the Billing tab.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Users List */}
            <Card className="card-industrial">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg">
                    {users.length} Users
                  </CardTitle>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="user-search"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {usersLoading ? (
                  <div className="animate-pulse h-48 bg-muted rounded" />
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-12">
                    <UsersIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No users found</p>
                    <Button onClick={() => handleOpenUserModal()} className="mt-4" variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Your First User
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="label-overline">Name</TableHead>
                          <TableHead className="label-overline">Username</TableHead>
                          <TableHead className="label-overline">Email</TableHead>
                          <TableHead className="label-overline">Role</TableHead>
                          <TableHead className="label-overline">Status</TableHead>
                          <TableHead className="label-overline text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUsers.map((u) => (
                          <TableRow key={u.id} data-testid={`user-row-${u.id}`} className={u.is_active === false ? 'opacity-60' : ''}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {u.full_name}
                                {u.id === user?.id && (
                                  <Badge variant="outline" className="text-xs">You</Badge>
                                )}
                                {u.must_change_password && (
                                  <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">
                                    <KeyRound className="h-3 w-3 mr-1" />
                                    Must change password
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="font-mono text-sm">{u.username}</TableCell>
                            <TableCell className="text-muted-foreground">{u.email || '-'}</TableCell>
                            <TableCell>{getRoleBadge(u.role)}</TableCell>
                            <TableCell>
                              {u.is_active === false ? (
                                <Badge variant="secondary" className="bg-red-100 text-red-800">
                                  <UserX className="h-3 w-3 mr-1" />
                                  Inactive
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="bg-green-100 text-green-800">
                                  <UserCheck className="h-3 w-3 mr-1" />
                                  Active
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {u.id !== user?.id ? (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0"
                                      data-testid={`user-actions-${u.id}`}
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => handleOpenUserModal(u)}>
                                      <Edit className="h-4 w-4 mr-2" />
                                      Edit User
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleResetPassword(u.id)}>
                                      <KeyRound className="h-4 w-4 mr-2" />
                                      Reset Password
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleViewActivity(u)}>
                                      <History className="h-4 w-4 mr-2" />
                                      View Activity
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleToggleUserStatus(u.id, u.is_active !== false)}>
                                      {u.is_active === false ? (
                                        <>
                                          <UserCheck className="h-4 w-4 mr-2" />
                                          Activate User
                                        </>
                                      ) : (
                                        <>
                                          <UserX className="h-4 w-4 mr-2" />
                                          Deactivate User
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <DropdownMenuItem
                                          onSelect={(e) => e.preventDefault()}
                                          className="text-destructive focus:text-destructive"
                                          data-testid={`delete-user-${u.id}`}
                                        >
                                          <Trash2 className="h-4 w-4 mr-2" />
                                          Delete User
                                        </DropdownMenuItem>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>Delete User</AlertDialogTitle>
                                          <AlertDialogDescription>
                                            Are you sure you want to delete <strong>{u.full_name}</strong>? 
                                            This action cannot be undone.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                                          <AlertDialogAction
                                            onClick={() => handleDeleteUser(u.id)}
                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                          >
                                            Delete
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              ) : (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

          {/* Add/Edit User Modal */}
          <Dialog open={userModalOpen} onOpenChange={setUserModalOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
                <DialogDescription>
                  {editingUser 
                    ? 'Update user details below.' 
                    : 'Create a new admin or dispatcher user. A temporary password will be generated.'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      value={userForm.full_name}
                      onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="username">Username *</Label>
                    <Input
                      id="username"
                      value={userForm.username}
                      onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                      placeholder="jsmith"
                      disabled={!!editingUser}
                      required
                    />
                    {editingUser && (
                      <p className="text-xs text-muted-foreground">Username cannot be changed</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role *</Label>
                    <Select
                      value={userForm.role}
                      onValueChange={(value) => setUserForm({ ...userForm, role: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-purple-600" />
                            Admin - Full access
                          </div>
                        </SelectItem>
                        <SelectItem value="dispatcher">
                          <div className="flex items-center gap-2">
                            <UserCog className="h-4 w-4 text-blue-600" />
                            Dispatcher - Schedule & manage jobs
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={handleCloseUserModal}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-volt" disabled={userFormLoading}>
                    {userFormLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {editingUser ? 'Saving...' : 'Creating...'}
                      </>
                    ) : (
                      editingUser ? 'Save Changes' : 'Create User'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Temporary Password Modal */}
          <Dialog open={tempPasswordModal.open} onOpenChange={(open) => setTempPasswordModal({ ...tempPasswordModal, open })}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Temporary Password Generated
                </DialogTitle>
                <DialogDescription>
                  Share this temporary password with the user. They will be required to change it on first login.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <div className="bg-muted p-4 rounded-lg space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Username</Label>
                    <p className="font-mono font-medium">{tempPasswordModal.username}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Temporary Password</Label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-background px-3 py-2 rounded font-mono text-lg">
                        {tempPasswordModal.password}
                      </code>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => copyToClipboard(tempPasswordModal.password)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2 mt-4 text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p>Make sure to copy this password now. It will not be shown again.</p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => setTempPasswordModal({ open: false, password: '', username: '' })} className="btn-volt">
                  Done
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Activity Log Modal */}
          <Dialog open={activityModal.open} onOpenChange={(open) => setActivityModal({ ...activityModal, open })}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Activity Log - {activityModal.user?.full_name}
                </DialogTitle>
                <DialogDescription>
                  Recent activity for this user account
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 max-h-96 overflow-y-auto">
                {activityLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : activityModal.logs.length === 0 ? (
                  <div className="text-center py-8">
                    <History className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">No activity logs found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activityModal.logs.map((log) => {
                      const actionInfo = formatActivityAction(log.action);
                      return (
                        <div key={log.id} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                          <div className={`p-1.5 rounded-full bg-background ${actionInfo.color}`}>
                            <History className="h-3 w-3" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${actionInfo.color}`}>
                              {actionInfo.label}
                            </p>
                            {log.details && (
                              <p className="text-xs text-muted-foreground mt-0.5">{log.details}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(log.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setActivityModal({ open: false, user: null, logs: [] })}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
          )}
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="mt-6">
          <div className="grid gap-6">
            {billingLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : billingData ? (
              <>
                {/* Pending Downgrade Banner */}
                {billingData.pending_tier_change && (
                  <Card className="card-industrial border-2 border-amber-500 bg-amber-500/10 mb-4">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-3">
                        <Clock className="h-5 w-5 text-amber-500" />
                        <div>
                          <p className="font-medium">
                            Downgrade to {billingData.pending_tier_change.new_tier_name} scheduled
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Your current {billingData.tier_name} features will remain active until{' '}
                            {billingData.pending_tier_change.effective_date 
                              ? new Date(billingData.pending_tier_change.effective_date).toLocaleDateString('en-AU', { 
                                  day: 'numeric', month: 'long', year: 'numeric' 
                                })
                              : 'your next billing cycle'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Trial Period Banner */}
                {billingData.trial && billingData.trial.is_trial && (
                  <Card className="card-industrial border-2 border-green-500 bg-green-500/10 mb-4">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Gift className="h-5 w-5 text-green-500" />
                          <div>
                            <p className="font-medium text-green-600">
                              Free Trial Active
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Your trial ends on {billingData.trial.trial_end_formatted}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-green-600">
                            {billingData.trial.days_remaining}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            days remaining
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Current Plan */}
                <Card className="card-industrial border-2 border-primary">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                          <Crown className="h-5 w-5 text-[#ffd108]" />
                          Current Plan: {billingData.tier_name}
                        </CardTitle>
                        <CardDescription>{billingData.tier_description}</CardDescription>
                      </div>
                      <div className="text-right">
                        <Badge variant="default" className="text-lg px-4 py-1">
                          ${(
                            (billingData.base_price || 0) + 
                            (billingData.operators_extra || 0) * (billingData.extra_operator_price || 0) + 
                            (billingData.users_extra || 0) * (billingData.extra_user_price || 0)
                          ).toFixed(0)}/month
                        </Badge>
                        {(billingData.operators_extra > 0 || billingData.users_extra > 0) && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Base ${billingData.base_price}
                            {billingData.operators_extra > 0 && ` + ${billingData.operators_extra}×$${billingData.extra_operator_price}`}
                            {billingData.users_extra > 0 && ` + ${billingData.users_extra}×$${billingData.extra_user_price}`}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Mobile Operators Usage */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Mobile Operators</span>
                          <span className="text-sm text-muted-foreground">
                            {billingData.operators_used || 0} / {billingData.operators_total_limit || 0}
                            {(billingData.operators_extra || 0) > 0 && (
                              <span className="text-xs text-primary ml-1">(+{billingData.operators_extra} extra)</span>
                            )}
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${
                              ((billingData.operators_used || 0) / (billingData.operators_total_limit || 1)) > 0.9 
                                ? 'bg-destructive' 
                                : 'bg-primary'
                            }`}
                            style={{ width: `${Math.min(((billingData.operators_used || 0) / (billingData.operators_total_limit || 1)) * 100, 100)}%` }}
                          />
                        </div>
                        {(billingData.operators_used || 0) >= (billingData.operators_total_limit || 1) && (
                          <p className="text-xs text-destructive">Mobile operator limit reached! Purchase more or upgrade your plan.</p>
                        )}
                      </div>

                      {/* Users Usage */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Admin/Dispatcher Users</span>
                          <span className="text-sm text-muted-foreground">
                            {billingData.users_used || 0} / {billingData.users_total_limit || 0}
                            {(billingData.users_extra || 0) > 0 && (
                              <span className="text-xs text-primary ml-1">(+{billingData.users_extra} extra)</span>
                            )}
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${
                              ((billingData.users_used || 0) / (billingData.users_total_limit || 1)) > 0.9 
                                ? 'bg-destructive' 
                                : 'bg-primary'
                            }`}
                            style={{ width: `${Math.min(((billingData.users_used || 0) / (billingData.users_total_limit || 1)) * 100, 100)}%` }}
                          />
                        </div>
                        {(billingData.users_used || 0) >= (billingData.users_total_limit || 1) && (
                          <p className="text-xs text-destructive">Limit reached! Purchase more or upgrade your plan.</p>
                        )}
                      </div>
                    </div>

                    {/* Features */}
                    <div className="mt-6">
                      <h4 className="text-sm font-medium mb-3">Included Features</h4>
                      <div className="flex flex-wrap gap-2">
                        {(billingData.features || []).map((feature) => (
                          feature && (
                          <Badge key={feature} variant="secondary" className="capitalize">
                            <Check className="h-3 w-3 mr-1" />
                            {feature.replace(/_/g, ' ')}{feature === 'api_access' ? ' (Coming Soon)' : ''}
                          </Badge>
                          )
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Storage Usage */}
                {storageData && (
                  <Card className="card-industrial">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="font-heading text-lg flex items-center gap-2">
                            <HardDrive className="h-5 w-5" />
                            Storage Usage
                          </CardTitle>
                          <CardDescription>Document storage for compliance, dockets, and more</CardDescription>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold">
                            {storageData.used_formatted || '0 bytes'}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            of {storageData.total_limit_formatted || '0 GB'}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Storage Progress Bar */}
                      <div className="space-y-2">
                        <div className="h-3 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${
                              storageData.is_full 
                                ? 'bg-destructive' 
                                : storageData.is_warning 
                                  ? 'bg-amber-500' 
                                  : 'bg-primary'
                            }`}
                            style={{ width: `${Math.min(storageData.usage_percent || 0, 100)}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>{storageData.usage_percent || 0}% used</span>
                          <span>{storageData.available_formatted || '0 GB'} available</span>
                        </div>
                      </div>

                      {/* Warning Message */}
                      {storageData.is_warning && !storageData.is_full && (
                        <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                          <p className="text-sm text-amber-600 flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4" />
                            You're running low on storage. Consider adding more to avoid upload issues.
                          </p>
                        </div>
                      )}

                      {/* Full Storage Message */}
                      {storageData.is_full && (
                        <div className="mt-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
                          <p className="text-sm text-destructive flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4" />
                            Storage limit reached! You cannot upload new files until you add more storage.
                          </p>
                        </div>
                      )}

                      {/* Storage Breakdown */}
                      <div className="mt-4 pt-4 border-t">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-muted-foreground">Plan Storage ({billingData?.tier_name})</span>
                          <span>{storageData.base_limit_formatted}</span>
                        </div>
                        {(storageData.addon_count || 0) > 0 && (
                          <div className="flex justify-between text-sm mb-2">
                            <span className="text-muted-foreground">Add-on Storage ({storageData.addon_count}× 50GB)</span>
                            <span>{storageData.addon_formatted || '0 bytes'}</span>
                          </div>
                        )}
                      </div>

                      {/* Add Storage Button */}
                      {storageData.addon_available && (
                        <Button 
                          onClick={handlePurchaseStorage}
                          disabled={checkoutLoading}
                          className="w-full mt-4"
                          variant={storageData.is_warning || storageData.is_full ? "default" : "outline"}
                        >
                          {checkoutLoading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Plus className="h-4 w-4 mr-2" />
                          )}
                          Add 50GB Storage
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Stripe Subscriptions Detail - Shows actual billing from Stripe */}
                {billingData.subscriptions && billingData.subscriptions.length > 0 && (
                  <Card className="card-industrial">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <CreditCard className="h-5 w-5" />
                        Subscription Details
                      </CardTitle>
                      <CardDescription>Your active subscriptions from Stripe</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {billingData.subscriptions.map((sub, index) => (
                          <div key={sub.id || index} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <Badge variant={sub.status === 'active' ? 'default' : 'secondary'} className={sub.status === 'active' ? 'bg-green-500' : ''}>
                                  {sub.status}
                                </Badge>
                                {sub.cancel_at_period_end && (
                                  <Badge variant="destructive">Cancels at period end</Badge>
                                )}
                              </div>
                              {sub.current_period_end && (
                                <span className="text-sm text-muted-foreground">
                                  Renews: {new Date(sub.current_period_end * 1000).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            
                            {/* Current Items */}
                            <div className="space-y-2">
                              {(sub.items || []).map((item, itemIndex) => (
                                <div key={itemIndex} className="flex items-center justify-between py-2 border-b last:border-0">
                                  <div>
                                    <span className="font-medium">{item.product_name || 'Subscription Item'}</span>
                                    {item.quantity > 1 && <span className="text-muted-foreground ml-2">×{item.quantity}</span>}
                                  </div>
                                  <span className="font-mono">
                                    ${item.amount?.toFixed(2) || '0.00'}/{item.interval || 'month'}
                                    {item.quantity > 1 && ` ($${(item.amount * item.quantity)?.toFixed(2) || '0.00'}/${item.interval || 'month'})`}
                                  </span>
                                </div>
                              ))}
                            </div>
                            
                            {/* Subscription Schedule - Shows upcoming price changes */}
                            {sub.schedule && sub.schedule.phases && sub.schedule.phases.length > 0 && (
                              <div className="mt-4 pt-4 border-t">
                                <h5 className="text-sm font-medium mb-3 flex items-center gap-2">
                                  <Clock className="h-4 w-4" />
                                  Billing Schedule
                                </h5>
                                <div className="space-y-2">
                                  {sub.schedule.phases.map((phase, phaseIndex) => {
                                    const isCurrent = sub.schedule.current_phase_index === phaseIndex;
                                    const isPast = sub.schedule.current_phase_index !== null && phaseIndex < sub.schedule.current_phase_index;
                                    const isFuture = sub.schedule.current_phase_index !== null && phaseIndex > sub.schedule.current_phase_index;
                                    
                                    return (
                                      <div 
                                        key={phaseIndex} 
                                        className={`p-3 rounded-lg border ${isCurrent ? 'border-primary bg-primary/5' : isPast ? 'opacity-50' : 'border-dashed'}`}
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex items-center gap-2">
                                            {isCurrent && <Badge className="bg-primary">Current</Badge>}
                                            {isFuture && <Badge variant="outline">Upcoming</Badge>}
                                            {isPast && <Badge variant="secondary">Completed</Badge>}
                                            <span className="text-sm">
                                              {phase.start_date && new Date(phase.start_date * 1000).toLocaleDateString()}
                                              {phase.end_date && ` - ${new Date(phase.end_date * 1000).toLocaleDateString()}`}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="space-y-1">
                                          {(phase.items || []).map((phaseItem, itemIdx) => (
                                            <div key={itemIdx} className="flex justify-between text-sm">
                                              <span className="text-muted-foreground">{phaseItem.product_name || 'Item'}</span>
                                              <span className="font-mono font-medium">
                                                ${phaseItem.amount?.toFixed(2) || '0.00'}/month
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Manage Extra Resources */}
                <Card className="card-industrial">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <SettingsIcon className="h-5 w-5" />
                      Manage Extra Resources
                    </CardTitle>
                    <CardDescription>Add or remove extra operators and users from your subscription</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Extra Operators */}
                      <div className="border rounded-lg p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Extra Operators</h4>
                            <p className="text-sm text-muted-foreground">${billingData.extra_operator_price}/operator/month</p>
                          </div>
                          <Badge variant="outline" className="text-lg px-3 py-1">
                            {billingData.operators_extra} active
                          </Badge>
                        </div>
                        
                        {billingData.operators_extra > 0 && (
                          <div className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                            <span className="text-sm">Adjust quantity:</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleModifyExtras('operator', Math.max(0, billingData.operators_extra - 1))}
                              disabled={checkoutLoading || billingData.operators_extra === 0}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-8 text-center font-mono">{billingData.operators_extra}</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleModifyExtras('operator', billingData.operators_extra + 1)}
                              disabled={checkoutLoading}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        
                        <div className="border-t pt-3">
                          <p className="text-sm text-muted-foreground mb-2">Add more operators:</p>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              max="50"
                              value={extraResourceQuantity}
                              onChange={(e) => setExtraResourceQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                              className="w-20"
                            />
                            <span className="text-sm text-muted-foreground">
                              = ${(billingData.extra_operator_price * extraResourceQuantity).toFixed(2)}/month
                            </span>
                          </div>
                          <Button 
                            onClick={() => billingData.stripe_customer_id 
                              ? handleModifyExtras('operator', billingData.operators_extra + extraResourceQuantity)
                              : handlePurchaseExtra('operator', extraResourceQuantity)
                            }
                            disabled={checkoutLoading}
                            className="w-full mt-2"
                            variant="outline"
                          >
                            {checkoutLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                            Add {extraResourceQuantity} Operator{extraResourceQuantity > 1 ? 's' : ''}
                          </Button>
                        </div>
                      </div>

                      {/* Extra Users */}
                      <div className="border rounded-lg p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Extra Users</h4>
                            <p className="text-sm text-muted-foreground">${billingData.extra_user_price}/user/month</p>
                          </div>
                          <Badge variant="outline" className="text-lg px-3 py-1">
                            {billingData.users_extra} active
                          </Badge>
                        </div>
                        
                        {billingData.users_extra > 0 && (
                          <div className="flex items-center gap-2 p-2 bg-muted/50 rounded">
                            <span className="text-sm">Adjust quantity:</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleModifyExtras('user', Math.max(0, billingData.users_extra - 1))}
                              disabled={checkoutLoading || billingData.users_extra === 0}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-8 text-center font-mono">{billingData.users_extra}</span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleModifyExtras('user', billingData.users_extra + 1)}
                              disabled={checkoutLoading}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        
                        <div className="border-t pt-3">
                          <p className="text-sm text-muted-foreground mb-2">Add more users:</p>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1"
                              max="50"
                              value={extraResourceQuantity}
                              onChange={(e) => setExtraResourceQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                              className="w-20"
                            />
                            <span className="text-sm text-muted-foreground">
                              = ${(billingData.extra_user_price * extraResourceQuantity).toFixed(2)}/month
                            </span>
                          </div>
                          <Button 
                            onClick={() => billingData.stripe_customer_id 
                              ? handleModifyExtras('user', billingData.users_extra + extraResourceQuantity)
                              : handlePurchaseExtra('user', extraResourceQuantity)
                            }
                            disabled={checkoutLoading}
                            className="w-full mt-2"
                            variant="outline"
                          >
                            {checkoutLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                            Add {extraResourceQuantity} User{extraResourceQuantity > 1 ? 's' : ''}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Available Plans */}
                {allTiers && billingData && (
                  <Card className="card-industrial">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        Change Your Plan
                      </CardTitle>
                      <CardDescription>Upgrade or downgrade your subscription. Extra operators and users will be migrated to the new plan's pricing.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {Object.entries(allTiers).map(([tierId, tierConfig]) => {
                          const isCurrentTier = tierId === (billingData.tier || 'basic');
                          const tierOrder = ['basic', 'advanced', 'professional'];
                          const currentTierIndex = tierOrder.indexOf(billingData.tier || 'basic');
                          const thisTierIndex = tierOrder.indexOf(tierId);
                          const isUpgrade = thisTierIndex > currentTierIndex;
                          const isDowngrade = thisTierIndex < currentTierIndex;
                          
                          return (
                            <div 
                              key={tierId}
                              className={`border rounded-lg p-4 ${
                                isCurrentTier ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-heading font-semibold">{tierConfig.name}</h4>
                                {isCurrentTier && <Badge>Current</Badge>}
                                {isUpgrade && <Badge variant="outline" className="text-green-600 border-green-600">Upgrade</Badge>}
                                {isDowngrade && <Badge variant="outline" className="text-orange-600 border-orange-600">Downgrade</Badge>}
                              </div>
                              <p className="text-2xl font-bold mb-2">${tierConfig.base_price}<span className="text-sm font-normal text-muted-foreground">/month</span></p>
                              <p className="text-sm text-muted-foreground mb-4">{tierConfig.description}</p>
                              
                              <div className="space-y-2 text-sm mb-4">
                                <div className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-green-500" />
                                  <span>{tierConfig.operator_limit} mobile operators included</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-green-500" />
                                  <span>{tierConfig.user_limit} user included</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-green-500" />
                                  <span>{tierConfig.features?.length || 0} features</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>Extra mobile operator: ${tierConfig.extra_operator_price}/mo</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>Extra user: ${tierConfig.extra_user_price}/mo</span>
                                </div>
                              </div>
                              
                              {isCurrentTier ? (
                                <Button disabled className="w-full" variant="outline">
                                  Current Plan
                                </Button>
                              ) : billingData.pending_tier_change?.new_tier === tierId ? (
                                <Button disabled className="w-full" variant="outline">
                                  <Clock className="h-4 w-4 mr-2" />
                                  Scheduled
                                </Button>
                              ) : billingData.stripe_customer_id ? (
                                <Button 
                                  onClick={() => handleChangePlan(tierId)}
                                  disabled={checkoutLoading}
                                  className="w-full"
                                  variant={isUpgrade ? "default" : "outline"}
                                >
                                  {checkoutLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : isUpgrade ? <Zap className="h-4 w-4 mr-2" /> : <ArrowDown className="h-4 w-4 mr-2" />}
                                  {isUpgrade ? 'Upgrade' : 'Downgrade'} to {tierConfig.name}
                                </Button>
                              ) : (
                                <Button 
                                  onClick={() => handleSubscriptionUpgrade(tierId)}
                                  disabled={checkoutLoading}
                                  className="w-full"
                                >
                                  {checkoutLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                                  Subscribe to {tierConfig.name}
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Transaction History */}
                {transactions.length > 0 && (
                  <Card className="card-industrial">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <CreditCard className="h-5 w-5" />
                        Transaction History
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {transactions.slice(0, 10).map((tx) => (
                            <TableRow key={tx.id}>
                              <TableCell>{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : '-'}</TableCell>
                              <TableCell className="capitalize">{(tx.transaction_type || '').replace(/_/g, ' ') || '-'}</TableCell>
                              <TableCell>${(tx.amount || 0).toFixed(2)}</TableCell>
                              <TableCell>
                                <Badge variant={tx.payment_status === 'paid' ? 'default' : tx.payment_status === 'pending' ? 'secondary' : 'destructive'}>
                                  {tx.payment_status || 'unknown'}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="card-industrial">
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">Failed to load billing information</p>
                  <Button onClick={fetchBillingData} variant="outline" className="mt-4">
                    Retry
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Accounting Tab - Xero and MYOB Integration */}
        <TabsContent value="accounting" className="mt-6">
          <div className="grid gap-6" data-testid="accounting-integration">
            {/* Info Banner - Only one accounting package at a time */}
            {(xeroStatus.connected || myobStatus.connected) && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-800 dark:text-blue-200 text-sm">
                      {connectedAccountingPackage === 'xero' ? 'Connected to Xero' : connectedAccountingPackage === 'myob' ? 'Connected to MYOB' : 'Accounting Connected'}
                    </p>
                    <p className="text-blue-700 dark:text-blue-300 text-sm mt-1">
                      Only one accounting package can be connected at a time. Connecting to a different package will disconnect the current one.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Xero Connection Card */}
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#13B5EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Xero Integration
                </CardTitle>
                <CardDescription>Connect your Xero account to push approved dockets as invoices</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {xeroLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : xeroStatus.connected ? (
                  <div className="space-y-4">
                    {/* Connected Status */}
                    <div className="flex items-center gap-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-full">
                        <CheckCircle2 className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-green-800 dark:text-green-200">Connected to Xero</p>
                        <p className="text-sm text-green-600 dark:text-green-400">
                          Organization: <span className="font-semibold">{xeroStatus.tenant_name}</span>
                        </p>
                        {xeroStatus.connected_at && (
                          <p className="text-xs text-green-500 dark:text-green-500 mt-1">
                            Connected on {new Date(xeroStatus.connected_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      {user?.role === 'admin' && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">
                              <Unlink className="h-4 w-4 mr-2" />
                              Disconnect
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Disconnect Xero?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will disconnect your Xero account. You won't be able to push dockets to Xero until you reconnect.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={handleDisconnectXero} className="bg-red-600 hover:bg-red-700">
                                Disconnect
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>

                    {/* Customer Sync Section */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium flex items-center gap-2">
                            <UsersIcon className="h-4 w-4 text-muted-foreground" />
                            Customer Sync
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Import customers from Xero to link with EQUIPR CORE.
                          </p>
                        </div>
                        <Button
                          onClick={handleImportCustomersFromXero}
                          disabled={xeroImporting}
                          variant="outline"
                          size="sm"
                        >
                          {xeroImporting ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Importing...
                            </>
                          ) : (
                            <>
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Import from Xero
                            </>
                          )}
                        </Button>
                      </div>
                      
                      {xeroImportResult && (
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                          <p className="text-sm text-green-800 dark:text-green-200 font-medium">Import Complete</p>
                          <div className="grid grid-cols-3 gap-4 mt-2 text-sm">
                            <div>
                              <span className="text-green-600">New:</span> <span className="font-semibold">{xeroImportResult.imported}</span>
                            </div>
                            <div>
                              <span className="text-green-600">Linked:</span> <span className="font-semibold">{xeroImportResult.linked}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Skipped:</span> <span className="font-semibold">{xeroImportResult.skipped}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-6 bg-muted/50 rounded-lg">
                    <div className="w-12 h-12 mx-auto mb-3 bg-[#13B5EA]/10 rounded-full flex items-center justify-center">
                      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#13B5EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h3 className="font-heading font-semibold mb-2">Connect Xero</h3>
                    <p className="text-muted-foreground text-sm mb-4 max-w-sm mx-auto">
                      Push approved dockets as draft invoices to Xero.
                    </p>
                    {user?.role === 'admin' ? (
                      <Button 
                        onClick={handleConnectXero}
                        disabled={xeroConnecting || myobStatus.connected}
                        className="bg-[#13B5EA] hover:bg-[#0d9fd1]"
                      >
                        {xeroConnecting ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          <>
                            <Link className="h-4 w-4 mr-2" />
                            Connect to Xero
                          </>
                        )}
                      </Button>
                    ) : (
                      <p className="text-sm text-amber-600">Admin required to connect</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* MYOB Connection Card */}
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="#6B21A8" strokeWidth="2"/>
                    <path d="M7 12h10M12 7v10" stroke="#6B21A8" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  MYOB Integration
                  {myobStatus.connected && (
                    <Badge className="bg-green-500/20 text-green-600 ml-2">Connected</Badge>
                  )}
                </CardTitle>
                <CardDescription>Connect your MYOB Business account to push approved dockets as invoices</CardDescription>
              </CardHeader>
              <CardContent>
                {myobLoading ? (
                  <div className="flex items-center justify-center p-6">
                    <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                  </div>
                ) : myobStatus.connected ? (
                  <div className="space-y-4">
                    {/* Reauth Warning Banner */}
                    {myobStatus.needs_reauth && (
                      <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="font-medium text-amber-800 dark:text-amber-200">Authentication Expired</p>
                            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                              {myobStatus.reauth_reason || 'Your MYOB connection needs to be re-authenticated. Please reconnect your account.'}
                            </p>
                            {user?.role === 'admin' && (
                              <Button 
                                onClick={handleConnectMyob}
                                disabled={myobConnecting}
                                className="mt-3 bg-amber-600 hover:bg-amber-700"
                                size="sm"
                              >
                                {myobConnecting ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Connecting...
                                  </>
                                ) : (
                                  <>
                                    <Link className="h-4 w-4 mr-2" />
                                    Reconnect MYOB
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Connection Info */}
                    <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/40 rounded-full flex items-center justify-center">
                          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="3" width="18" height="18" rx="2" stroke="#6B21A8" strokeWidth="2"/>
                            <path d="M7 12h10M12 7v10" stroke="#6B21A8" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-sm">{myobStatus.company_name || 'MYOB Company'}</p>
                          <p className="text-xs text-muted-foreground">
                            Connected {myobStatus.connected_at ? new Date(myobStatus.connected_at).toLocaleDateString() : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {myobStatus.product_type === 'accountright' && (
                          <Badge variant="outline" className="text-xs">AccountRight</Badge>
                        )}
                        {myobStatus.product_type === 'business' && (
                          <Badge variant="outline" className="text-xs">MYOB Business</Badge>
                        )}
                        {user?.role === 'admin' && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                <Unlink className="h-4 w-4 mr-1" />
                                Disconnect
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Disconnect MYOB?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will disconnect your MYOB account. You won't be able to push dockets to MYOB until you reconnect.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={handleDisconnectMyob} className="bg-red-600 hover:bg-red-700">
                                  Disconnect
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>

                    {/* AccountRight Credentials Section - Only show for AccountRight */}
                    {myobStatus.product_type === 'accountright' && user?.role === 'admin' && (
                      <div className="border rounded-lg p-4 bg-muted/30">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-medium flex items-center gap-2">
                              <KeyRound className="h-4 w-4 text-muted-foreground" />
                              Company File Credentials
                            </h4>
                            <p className="text-sm text-muted-foreground mt-1">
                              {myobStatus.has_cf_credentials 
                                ? 'Credentials configured for API access' 
                                : 'No credentials configured - import may fail'}
                            </p>
                          </div>
                          {myobStatus.has_cf_credentials ? (
                            <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
                              <Check className="h-3 w-3 mr-1" /> Configured
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <AlertCircle className="h-3 w-3 mr-1" /> Missing
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <Edit className="h-4 w-4 mr-2" />
                                {myobStatus.has_cf_credentials ? 'Update Credentials' : 'Add Credentials'}
                              </Button>
                            </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>MYOB AccountRight Credentials</DialogTitle>
                              <DialogDescription>
                                Enter the username and password for your MYOB company file. This is required for API access to AccountRight.
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div>
                                <Label htmlFor="cf-username">Company File Username</Label>
                                <Input 
                                  id="cf-username"
                                  value={myobSettings.cf_username}
                                  onChange={(e) => setMyobSettings(s => ({ ...s, cf_username: e.target.value }))}
                                  placeholder="e.g., Administrator"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label htmlFor="cf-password">Company File Password</Label>
                                <Input 
                                  id="cf-password"
                                  type="password"
                                  value={myobSettings.cf_password}
                                  onChange={(e) => setMyobSettings(s => ({ ...s, cf_password: e.target.value }))}
                                  placeholder="Enter password"
                                  className="mt-1"
                                />
                              </div>
                              <details className="text-xs">
                                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                                  Where do I find these credentials?
                                </summary>
                                <div className="mt-2 p-3 bg-muted rounded text-muted-foreground space-y-2">
                                  <p>Company file users are set up within AccountRight:</p>
                                  <ol className="list-decimal list-inside space-y-1 ml-2">
                                    <li>Open your company file in MYOB AccountRight</li>
                                    <li>Go to Setup → Preferences → Security</li>
                                    <li>Create or use an existing user with API access</li>
                                  </ol>
                                </div>
                              </details>
                            </div>
                            <DialogFooter>
                              <Button 
                                onClick={async () => {
                                  try {
                                    await api.post('/myob/settings', {
                                      product_type: 'accountright',
                                      cf_username: myobSettings.cf_username,
                                      cf_password: myobSettings.cf_password
                                    });
                                    toast.success('Credentials updated successfully');
                                    fetchMyobStatus();
                                  } catch (error) {
                                    toast.error(error.response?.data?.detail || 'Failed to update credentials');
                                  }
                                }}
                                disabled={!myobSettings.cf_username || !myobSettings.cf_password}
                              >
                                Save Credentials
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                          <RouterLink to="/help?article=myob-setup" data-testid="myob-setup-guide-link">
                            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
                              <HelpCircle className="h-4 w-4 mr-2" />
                              View Setup Guide
                            </Button>
                          </RouterLink>
                        </div>
                      </div>
                    )}

                    {/* Customer Sync Section */}
                    <div className="border rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium flex items-center gap-2">
                            <UsersIcon className="h-4 w-4 text-muted-foreground" />
                            Customer Sync
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Import customers from MYOB to link with EQUIPR CORE.
                          </p>
                        </div>
                        <Button
                          onClick={handleImportCustomersFromMyob}
                          disabled={myobImporting}
                          variant="outline"
                          size="sm"
                        >
                          {myobImporting ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Importing...
                            </>
                          ) : (
                            <>
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Import from MYOB
                            </>
                          )}
                        </Button>
                      </div>
                      
                      {myobImportResult && (
                        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                          <p className="text-sm text-green-800 dark:text-green-200 font-medium">Import Complete</p>
                          <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                            <div>
                              <span className="text-green-600">New:</span> <span className="font-semibold">{myobImportResult.imported}</span>
                            </div>
                            <div>
                              <span className="text-green-600">Updated:</span> <span className="font-semibold">{myobImportResult.updated}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-6 bg-muted/50 rounded-lg">
                    {!showMyobSetup ? (
                      <>
                        <div className="w-12 h-12 mx-auto mb-3 bg-purple-100 dark:bg-purple-900/40 rounded-full flex items-center justify-center">
                          <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="3" width="18" height="18" rx="2" stroke="#6B21A8" strokeWidth="2"/>
                            <path d="M7 12h10M12 7v10" stroke="#6B21A8" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        </div>
                        <h3 className="font-heading font-semibold mb-2">Connect MYOB</h3>
                        <p className="text-muted-foreground text-sm mb-4 max-w-sm mx-auto">
                          Push approved dockets as draft invoices to MYOB Business or AccountRight.
                        </p>
                        {user?.role === 'admin' ? (
                          <Button 
                            onClick={handleStartMyobSetup}
                            disabled={xeroStatus.connected}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            <Link className="h-4 w-4 mr-2" />
                            Set Up MYOB Connection
                          </Button>
                        ) : (
                          <p className="text-sm text-amber-600">Admin required to connect</p>
                        )}
                        {xeroStatus.connected && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Disconnect Xero to connect MYOB
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="text-left max-w-md mx-auto space-y-6">
                        <div>
                          <h3 className="font-heading font-semibold mb-1">MYOB Product Setup</h3>
                          <p className="text-muted-foreground text-sm">
                            Select your MYOB product and configure access settings.
                          </p>
                        </div>

                        {/* Product Type Selection */}
                        <div className="space-y-3">
                          <label className="text-sm font-medium">Which MYOB product do you use?</label>
                          <div className="grid gap-3">
                            <div 
                              className={`p-4 border rounded-lg cursor-pointer transition-colors ${myobSettings.product_type === 'business' ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-border hover:border-purple-300'}`}
                              onClick={() => setMyobSettings(s => ({ ...s, product_type: 'business' }))}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-4 h-4 rounded-full border-2 ${myobSettings.product_type === 'business' ? 'border-purple-500 bg-purple-500' : 'border-gray-300'}`}>
                                  {myobSettings.product_type === 'business' && <div className="w-full h-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                                </div>
                                <div>
                                  <p className="font-medium">MYOB Business</p>
                                  <p className="text-xs text-muted-foreground">Cloud-based accounting (formerly Essentials)</p>
                                </div>
                              </div>
                            </div>
                            <div 
                              className={`p-4 border rounded-lg cursor-pointer transition-colors ${myobSettings.product_type === 'accountright' ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-border hover:border-purple-300'}`}
                              onClick={() => setMyobSettings(s => ({ ...s, product_type: 'accountright' }))}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-4 h-4 rounded-full border-2 ${myobSettings.product_type === 'accountright' ? 'border-purple-500 bg-purple-500' : 'border-gray-300'}`}>
                                  {myobSettings.product_type === 'accountright' && <div className="w-full h-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                                </div>
                                <div>
                                  <p className="font-medium">MYOB AccountRight</p>
                                  <p className="text-xs text-muted-foreground">Desktop/hosted accounting software</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* AccountRight Credentials */}
                        {myobSettings.product_type === 'accountright' && (
                          <div className="space-y-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="font-medium text-amber-800 dark:text-amber-200 text-sm">Company File Credentials Required</p>
                                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                                  AccountRight requires a company file user for API access. This is different from your my.myob.com login.
                                </p>
                              </div>
                            </div>
                            
                            <div className="space-y-3">
                              <div>
                                <label className="text-sm font-medium block mb-1">Company File Username</label>
                                <Input 
                                  value={myobSettings.cf_username}
                                  onChange={(e) => setMyobSettings(s => ({ ...s, cf_username: e.target.value }))}
                                  placeholder="e.g., Administrator"
                                  className="bg-white dark:bg-gray-900"
                                />
                              </div>
                              <div>
                                <label className="text-sm font-medium block mb-1">Company File Password</label>
                                <Input 
                                  type="password"
                                  value={myobSettings.cf_password}
                                  onChange={(e) => setMyobSettings(s => ({ ...s, cf_password: e.target.value }))}
                                  placeholder="Enter password"
                                  className="bg-white dark:bg-gray-900"
                                />
                              </div>
                            </div>

                            <details className="text-xs">
                              <summary className="cursor-pointer text-amber-700 dark:text-amber-300 hover:underline">
                                How do I find my company file credentials?
                              </summary>
                              <div className="mt-2 p-3 bg-white dark:bg-gray-900 rounded border text-muted-foreground space-y-2">
                                <p><strong>Company file users</strong> are set up within AccountRight itself:</p>
                                <ol className="list-decimal list-inside space-y-1 ml-2">
                                  <li>Open your company file in MYOB AccountRight</li>
                                  <li>Go to <strong>Setup → Preferences → Security</strong></li>
                                  <li>Create or use an existing user with API access</li>
                                  <li>The default is often "Administrator" with a blank password</li>
                                </ol>
                                <p className="mt-2 text-amber-600">Note: We recommend creating a dedicated API user for security.</p>
                              </div>
                            </details>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-2">
                          <Button 
                            variant="outline"
                            onClick={() => setShowMyobSetup(false)}
                            disabled={myobSettingsSaving}
                          >
                            Cancel
                          </Button>
                          <Button 
                            onClick={handleSaveMyobSettingsAndConnect}
                            disabled={myobSettingsSaving || !myobSettings.product_type || (myobSettings.product_type === 'accountright' && (!myobSettings.cf_username || !myobSettings.cf_password))}
                            className="bg-purple-600 hover:bg-purple-700 flex-1"
                          >
                            {myobSettingsSaving ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Connecting...
                              </>
                            ) : (
                              <>
                                <Link className="h-4 w-4 mr-2" />
                                Connect to MYOB
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Invoice Numbering Setting - Show when any accounting package is connected */}
            {(xeroStatus.connected || myobStatus.connected) && (
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-muted-foreground" />
                    Invoice Numbering
                  </CardTitle>
                  <CardDescription>Choose how invoice numbers are assigned when exporting dockets</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div 
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${invoiceNumberingSource === 'equipr' ? 'border-[#ffd108] bg-[#ffd108]/5' : 'hover:bg-muted/50'}`}
                      onClick={() => handleSaveInvoiceNumberingSetting('equipr')}
                    >
                      <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${invoiceNumberingSource === 'equipr' ? 'border-[#ffd108]' : 'border-muted-foreground'}`}>
                        {invoiceNumberingSource === 'equipr' && <div className="w-2 h-2 rounded-full bg-[#ffd108]" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">EQUIPR CORE Assigned</p>
                        <p className="text-xs text-muted-foreground">Invoice numbers match Job/Docket numbers (e.g., Job #14 → Docket #14 → INV-00014)</p>
                      </div>
                    </div>
                    
                    <div 
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${invoiceNumberingSource === 'accounting_package' ? 'border-[#ffd108] bg-[#ffd108]/5' : 'hover:bg-muted/50'}`}
                      onClick={() => handleSaveInvoiceNumberingSetting('accounting_package')}
                    >
                      <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${invoiceNumberingSource === 'accounting_package' ? 'border-[#ffd108]' : 'border-muted-foreground'}`}>
                        {invoiceNumberingSource === 'accounting_package' && <div className="w-2 h-2 rounded-full bg-[#ffd108]" />}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{connectedAccountingPackage ? connectedAccountingPackage.toUpperCase() : 'Accounting Package'} Assigned</p>
                        <p className="text-xs text-muted-foreground">Let {connectedAccountingPackage?.toUpperCase() || 'your accounting software'} assign invoice numbers automatically</p>
                      </div>
                    </div>
                  </div>
                  
                  {savingInvoiceSetting && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-3">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* How it works - when no accounting package connected */}
            {!xeroStatus.connected && !myobStatus.connected && (
              <Card className="card-industrial">
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <HelpCircle className="h-5 w-5 text-muted-foreground" />
                    How it works
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <span>Approved dockets can be pushed to your accounting software as Draft invoices</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <span>Invoice will include customer, job reference, and docket items</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <span>Review and send invoices from within Xero or MYOB</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <span>Secure OAuth 2.0 authentication - we never see your password</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Timesheets Settings Tab */}
        <TabsContent value="timesheets" className="mt-6">
          <div className="grid gap-6">
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Pay Cycle Settings
                </CardTitle>
                <CardDescription>
                  Configure how timesheets are grouped for payroll processing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Pay Cycle Type */}
                <div className="space-y-2">
                  <Label htmlFor="pay_cycle_type">Pay Cycle Frequency</Label>
                  <Select
                    value={form.pay_cycle_type}
                    onValueChange={(value) => setForm({ ...form, pay_cycle_type: value, pay_cycle_day: value === 'monthly' ? 1 : 1 })}
                  >
                    <SelectTrigger id="pay_cycle_type" className="w-full md:w-[300px]">
                      <SelectValue placeholder="Select pay cycle frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="fortnightly">Fortnightly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    How often your pay cycle runs
                  </p>
                </div>

                {/* Pay Cycle Day - Weekly/Fortnightly */}
                {(form.pay_cycle_type === 'weekly' || form.pay_cycle_type === 'fortnightly') && (
                  <div className="space-y-2">
                    <Label htmlFor="pay_cycle_day">Pay Cycle Start Day</Label>
                    <Select
                      value={String(form.pay_cycle_day)}
                      onValueChange={(value) => setForm({ ...form, pay_cycle_day: parseInt(value) })}
                    >
                      <SelectTrigger id="pay_cycle_day" className="w-full md:w-[300px]">
                        <SelectValue placeholder="Select day of week" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Monday</SelectItem>
                        <SelectItem value="2">Tuesday</SelectItem>
                        <SelectItem value="3">Wednesday</SelectItem>
                        <SelectItem value="4">Thursday</SelectItem>
                        <SelectItem value="5">Friday</SelectItem>
                        <SelectItem value="6">Saturday</SelectItem>
                        <SelectItem value="7">Sunday</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      The day of the week your pay cycle begins
                    </p>
                  </div>
                )}

                {/* Pay Cycle Day - Monthly */}
                {form.pay_cycle_type === 'monthly' && (
                  <div className="space-y-2">
                    <Label htmlFor="pay_cycle_day_monthly">Pay Cycle Start Day of Month</Label>
                    <Select
                      value={String(form.pay_cycle_day)}
                      onValueChange={(value) => setForm({ ...form, pay_cycle_day: parseInt(value) })}
                    >
                      <SelectTrigger id="pay_cycle_day_monthly" className="w-full md:w-[300px]">
                        <SelectValue placeholder="Select day of month" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                          <SelectItem key={day} value={String(day)}>
                            {day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'} of each month
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      The day of the month your pay cycle begins (1-28 to avoid month-end issues)
                    </p>
                  </div>
                )}

                {/* Fortnightly Reference Date */}
                {form.pay_cycle_type === 'fortnightly' && (
                  <div className="space-y-2">
                    <Label htmlFor="pay_cycle_start_date">Fortnightly Reference Start Date</Label>
                    <Input
                      id="pay_cycle_start_date"
                      type="date"
                      value={form.pay_cycle_start_date || ''}
                      onChange={(e) => setForm({ ...form, pay_cycle_start_date: e.target.value })}
                      className="w-full md:w-[300px]"
                    />
                    <p className="text-xs text-muted-foreground">
                      A reference date to anchor fortnightly calculations. Choose any past date when a pay cycle started.
                    </p>
                  </div>
                )}

                {/* Preview */}
                <div className="p-4 bg-muted/50 rounded-lg border">
                  <h4 className="font-medium mb-2">Current Configuration</h4>
                  <p className="text-sm text-muted-foreground">
                    {form.pay_cycle_type === 'weekly' && (
                      <>Pay cycle runs <span className="font-medium text-foreground">weekly</span>, starting every <span className="font-medium text-foreground">{['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][form.pay_cycle_day]}</span></>
                    )}
                    {form.pay_cycle_type === 'fortnightly' && (
                      <>Pay cycle runs <span className="font-medium text-foreground">fortnightly</span>, starting every second <span className="font-medium text-foreground">{['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][form.pay_cycle_day]}</span></>
                    )}
                    {form.pay_cycle_type === 'monthly' && (
                      <>Pay cycle runs <span className="font-medium text-foreground">monthly</span>, starting on the <span className="font-medium text-foreground">{form.pay_cycle_day}{form.pay_cycle_day === 1 ? 'st' : form.pay_cycle_day === 2 ? 'nd' : form.pay_cycle_day === 3 ? 'rd' : 'th'}</span> of each month</>
                    )}
                  </p>
                </div>

                <Button 
                  onClick={handleSavePayCycle} 
                  disabled={savingPayCycle}
                  className="btn-volt"
                >
                  {savingPayCycle ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Save Pay Cycle Settings
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* My Account Tab */}
        <TabsContent value="account" className="mt-6">
          <div className="space-y-6">
            {/* Account Info */}
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Account Information
                </CardTitle>
                <CardDescription>Your personal account details</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Full Name</p>
                    <p className="font-medium">{user?.full_name}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Username</p>
                    <p className="font-mono">{user?.username}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Role</p>
                    {getRoleBadge(user?.role)}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Client ID</p>
                    <p className="font-mono">{user?.client_id}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Change Password */}
            <Card className="card-industrial">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <KeyRound className="h-5 w-5" />
                  Change Password
                </CardTitle>
                <CardDescription>Update your account password</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                  <div className="space-y-2">
                    <Label className="label-overline">Current Password</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords ? 'text' : 'password'}
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                        placeholder="Enter current password"
                        required
                        data-testid="current-password-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">New Password</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords ? 'text' : 'password'}
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                        placeholder="Enter new password"
                        required
                        data-testid="new-password-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">Confirm New Password</Label>
                    <Input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      placeholder="Confirm new password"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                  
                  {/* Password Requirements */}
                  <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Password Requirements:</p>
                    <div className="flex items-center gap-2 text-sm">
                      {passwordForm.newPassword.length >= 8 ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border border-muted-foreground" />
                      )}
                      <span className={passwordForm.newPassword.length >= 8 ? 'text-green-600' : 'text-muted-foreground'}>
                        At least 8 characters
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {/\d/.test(passwordForm.newPassword) ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border border-muted-foreground" />
                      )}
                      <span className={/\d/.test(passwordForm.newPassword) ? 'text-green-600' : 'text-muted-foreground'}>
                        Contains at least one number
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {passwordForm.newPassword && passwordForm.newPassword === passwordForm.confirmPassword ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border border-muted-foreground" />
                      )}
                      <span className={passwordForm.newPassword && passwordForm.newPassword === passwordForm.confirmPassword ? 'text-green-600' : 'text-muted-foreground'}>
                        Passwords match
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Button 
                      type="submit" 
                      className="btn-volt"
                      disabled={passwordLoading}
                      data-testid="change-password-btn"
                    >
                      {passwordLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Changing...
                        </>
                      ) : (
                        <>
                          <KeyRound className="h-4 w-4 mr-2" />
                          Change Password
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPasswords(!showPasswords)}
                    >
                      {showPasswords ? (
                        <><EyeOff className="h-4 w-4 mr-2" /> Hide</>
                      ) : (
                        <><Eye className="h-4 w-4 mr-2" /> Show</>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Support Tab */}
        <TabsContent value="support" className="mt-6">
          <SupportTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
